import React, { useState } from 'react';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
const outcomes = {
  expected: 'Na série esperada',
  sameGrade: 'Na mesma série',
  otherGrade: 'Em outra série',
  review: 'A conferir',
  missing: 'Não localizado'
};
export default function EnrollmentCohortReport({
  cohorts,
  year
}) {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const active = cohorts.find(row => row.name === selected);
  const visible = active?.details.filter(row => filter === 'all' || (filter === 'shift' ? row.changedShift : filter === 'sameShift' ? row.originTurns.length === 1 && row.destinationTurns.length === 1 && row.originTurns[0] !== 'Não informado' && row.originTurns[0] === row.destinationTurns[0] : row.status === filter)) || [];
  const open = row => {
    setSelected(row.name);
    setFilter('all');
  };
  return <Paper sx={{
    p: {
      xs: 2,
      md: 3
    },
    borderRadius: 3,
    border: '1px solid #e1e9f0',
    minWidth: 0
  }}>
    <Typography variant="h6" sx={{
      fontWeight: 700
    }}>Acompanhamento da rematrícula</Typography>
    <Typography variant="body2" color="text.secondary" sx={{
      my: 1
    }}>Alunos únicos de {year - 1} cruzados pelo identificador com a consulta de {year}. A próxima série esperada é conferida separadamente dos demais destinos. Mudanças de turno estão incluídas nos localizados e podem ser conferidas na lista.</Typography>
    <TableContainer sx={{
      overflowX: 'auto',
      border: '1px solid #e1e9f0',
      borderRadius: 2
    }}><Table size="small" aria-label="Acompanhamento da rematrícula" sx={{
        minWidth: 920,
        '& th': {
          bgcolor: '#eff5fa',
          fontWeight: 700
        },
        '& td, & th': {
          py: 1.5,
          fontFamily: 'inherit'
        },
        '& tbody tr:nth-of-type(even)': {
          bgcolor: '#f8fafc'
        }
      }}>
      <TableHead><TableRow>{[`Origem · ${year - 1}`, `Destino esperado · ${year}`, 'Alunos na base', 'Na série esperada', `Localizados em ${year}`, 'Trocaram turno¹', 'Não localizados'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
      <TableBody>{cohorts.map(row => <TableRow key={row.name}>
        <TableCell>{row.name}<Button size="small" onClick={() => open(row)} aria-label={`Conferir alunos de ${row.name}`} sx={{ display: 'block', px: 0 }}>Ver alunos</Button></TableCell><TableCell>{row.expectedName}{row.sameGrade + row.otherGrade + row.review > 0 && <Typography variant="caption" component="div" color="text.secondary">Mesma série: {row.sameGrade}; outras: {row.otherGrade}; a conferir: {row.review}.</Typography>}</TableCell><TableCell>{row.total}</TableCell><TableCell sx={{
              fontWeight: 700
            }}>{row.terminal || row.expectedName === 'Progressão não mapeada' ? '—' : row.expected}</TableCell><TableCell sx={{
              fontWeight: 700
            }}>{row.located}</TableCell><TableCell>{row.turnChanges}</TableCell><TableCell>{row.missing}</TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer>
    <Typography variant="caption" color="text.secondary" component="p" sx={{
      mb: 0
    }}>¹ Trocas de turno são parte dos localizados, não uma contagem adicional. Cada aluno entra uma única vez. Ausência na consulta não comprova evasão; concluintes do Ensino Médio não têm próxima série nesta oferta. A série esperada não comprova aprovação.</Typography>
    <Dialog open={Boolean(active)} onClose={() => setSelected(null)} fullWidth maxWidth="lg" aria-labelledby="cohort-details-title">
      <DialogTitle id="cohort-details-title">Conferência · {active?.name} de {year - 1}</DialogTitle>
      <DialogContent>
        {active && <>
          <Typography variant="body2" sx={{
            mb: 2
          }}>{active.total} alunos na base: {active.located} localizados e {active.missing} não localizados. Dos localizados, {active.expected} na série esperada, {active.sameGrade} na mesma série, {active.otherGrade} em outras séries e {active.review} a conferir. {active.turnChanges} troca(s) de turno.</Typography>
          <Alert severity="info" sx={{
            mb: 2
          }}>A conferência usa o identificador do aluno, não a semelhança de nomes. Os destinos são os contratos da situação selecionada no relatório. Destinos em séries diferentes ou séries não reconhecidas exigem conferência, sem presumir aprovação ou reprovação.</Alert>
          <FormControl size="small" sx={{
            minWidth: 220,
            mb: 2
          }}><InputLabel id="cohort-detail-filter">Mostrar alunos</InputLabel><Select labelId="cohort-detail-filter" label="Mostrar alunos" value={filter} onChange={event => setFilter(event.target.value)}><MenuItem value="all">Todos</MenuItem>{Object.entries(outcomes).map(([value, label]) => <MenuItem value={value} key={value}>{label}</MenuItem>)}<MenuItem value="shift">Com troca de turno</MenuItem><MenuItem value="sameShift">Sem troca de turno</MenuItem></Select></FormControl>
          <Typography variant="body2" sx={{
            mb: 1
          }}>{visible.length} de {active.total} alunos</Typography>
          <TableContainer><Table size="small" aria-label={`Alunos de ${active.name}`} sx={{
              minWidth: 850
            }}><TableHead><TableRow>{['Aluno', `Turma em ${year - 1}`, `Turno em ${year - 1}`, `Turma em ${year}`, `Turno em ${year}`, 'Resultado'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead><TableBody>{visible.map(row => <TableRow key={row.id}>
            <TableCell>{row.name}<Typography variant="caption" color="text.secondary" component="div">Identificador {row.id}</Typography></TableCell><TableCell>{row.originClasses.join('; ')}</TableCell><TableCell>{row.originTurns.join('; ')}</TableCell><TableCell>{row.destinationClasses.join('; ') || 'Não localizada na consulta'}</TableCell><TableCell>{row.destinationTurns.join('; ') || '—'}</TableCell><TableCell><Typography variant="body2">{outcomes[row.status]}</Typography>{row.changedShift && <Chip size="small" label="Mudou de turno" color="info" variant="outlined" />}</TableCell>
          </TableRow>)}</TableBody></Table></TableContainer>
        </>}
      </DialogContent>
      <DialogActions><Button onClick={() => setSelected(null)}>Fechar</Button></DialogActions>
    </Dialog>
  </Paper>;
}
